import { observer } from 'mobx-react';
import React from 'react';

import { GridLegacy as Grid } from '@mui/material';

import { Form, Formik, FormikHelpers, getIn } from 'formik';

import { AxiosError } from 'axios';

import { Button } from 'components/Button';
import NavigationBlocker from 'components/NavigationBlocker';

import { StyledTextField } from 'components/Input/styles';

import API from 'utils/api';

import { UpdatedUserPayload } from 'utils/events/types';

import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import { UserRole } from 'core/constants';

import { EditUserResponse, PersonalDetailsResponse } from 'utils/api/modules/user/user.types';

import { formErrors, isBadRequest } from 'utils/api/errors';

import Logger from 'utils/logger';

import { useStore } from '../../../../utils/hooks/store';
import { StyledSectionGrid, StyledRowTitle, ButtonWrapper } from './styles';
import { EditMedicalInformationSchema } from './types';

const EditRow: React.FunctionComponent<{
  name: string;
  disabled?: boolean;
  children: React.ReactNode;
  id?: string;
}> = ({ name, disabled = false, children, id }): React.ReactElement => (
  <>
    <StyledSectionGrid item container xs={12} alignContent="flex-start" id={id} marginTop="16px">
      <StyledRowTitle $disabled={disabled}>{name}</StyledRowTitle>
    </StyledSectionGrid>
    <StyledSectionGrid item container xs={12}>
      {children}
    </StyledSectionGrid>
  </>
);

export const MedicalInformation: React.FunctionComponent<{ setEditing: (val: boolean) => void }> = ({ setEditing }) => {
  const {
    userDetails: { details },
    notification,
    session,
  } = useStore();

  if (!details) {
    return null;
  }

  const onUpdate = async (
    data: PersonalDetailsResponse,
    helpers: FormikHelpers<PersonalDetailsResponse>
  ): Promise<void> => {
    try {
      helpers.setSubmitting(true);
      let formattedData: Partial<EditUserResponse> = {
        personalDetails: data,
      };

      if (session.user?.role === UserRole.User) {
        formattedData = {
          personalDetails: data,
        };
      }
      const response = await API.user.updateUserData(details.uuid, formattedData)();
      const payload: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, payload);
      notification.enqueueSuccessSnackbar('User details updated successfully!');

      helpers.setStatus();
      helpers.setErrors({});
      setEditing(false);
    } catch (e) {
      const error = e as AxiosError;
      notification.enqueueErrorSnackbar('Could not update user details');

      const { nonFieldErrors, fieldErrors } = formErrors(error);
      fieldErrors && helpers.setErrors(fieldErrors);
      nonFieldErrors && helpers.setStatus(nonFieldErrors);

      if (!isBadRequest(error)) {
        Logger.error('Invalid update user details API response', error);
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const fields = [
    ['medicalConditions', 'Medical Conditions'],
    ['allergies', 'Allergies'],
    ['currentMedication', 'Current Medication'],
    ['bloodType', 'Blood Type'],
    ['weight', 'Weight'],
    ['height', 'Height'],
  ];

  return (
    <Formik
      initialValues={details.personalDetails.initialValues}
      onSubmit={(values, helpers) => onUpdate(values, helpers)}
      validationSchema={EditMedicalInformationSchema}
    >
      {({ values, handleChange, touched, errors, isSubmitting, dirty }) => (
        <Form>
          <NavigationBlocker
            when={dirty}
            shouldBlockNavigation={() => dirty}
            navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
          />
          <Grid container sx={{ padding: '20px' }}>
            {fields.map(([property, name]) => (
              <EditRow key={property} name={name}>
                <StyledTextField
                  id={property}
                  name={property}
                  value={getIn(values, property)}
                  helperText={getIn(touched, property) ? getIn(errors, property) : ''}
                  error={Boolean(getIn(touched, property)) && getIn(errors, property) !== undefined}
                  onChange={handleChange}
                  fullWidth
                />
              </EditRow>
            ))}

            <ButtonWrapper>
              <Button variant="outlined" onClick={() => setEditing(false)} size="large">
                Cancel
              </Button>

              <Button variant="contained" type="submit" size="large" disabled={isSubmitting}>
                Save
              </Button>
            </ButtonWrapper>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default observer(MedicalInformation);
